import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { Aviso, Botao, Campo, Cartao, Entrada } from '../../componentes/ui.jsx';
import { useAuth } from '../../lib/autenticacao.jsx';
import { CHAVE_CANAIS } from './hooks.js';

/**
 * Credenciais da API oficial (WhatsApp Cloud API) de UM numero.
 *
 * O token e o App Secret so ENTRAM: o servidor guarda cifrado e devolve apenas
 * os 4 ultimos caracteres. Campo vazio = manter o que ja esta salvo.
 *
 * O endereco do webhook e o verify token sao o que se cola no painel da Meta
 * (WhatsApp → Configuração → Webhook). O endereco usa o dominio por onde esta
 * tela foi aberta: no servidor na nuvem, e o dominio publico do sistema.
 */
export function CredenciaisOficial({ canal }) {
  const { podeAcessar } = useAuth();
  const podeEditar = podeAcessar('admin');
  const clienteQuery = useQueryClient();
  const o = canal.oficial ?? {};

  const [form, setForm] = useState({ phoneNumberId: o.phoneNumberId ?? '', wabaId: o.wabaId ?? '', token: '', appSecret: '' });
  const [copiado, setCopiado] = useState(null);

  const salvar = useMutation({
    mutationFn: (corpo) => api.patch(`/api/canais/${encodeURIComponent(canal.chave)}/oficial`, corpo),
    onSuccess: () => setForm((f) => ({ ...f, token: '', appSecret: '' })),
    onSettled: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_CANAIS })
  });

  const mudar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  function gravar(e) {
    e.preventDefault();
    const corpo = {};
    if (form.phoneNumberId.trim() !== (o.phoneNumberId ?? '')) corpo.phoneNumberId = form.phoneNumberId.trim();
    if (form.wabaId.trim() !== (o.wabaId ?? '')) corpo.wabaId = form.wabaId.trim();
    if (form.token.trim()) corpo.token = form.token.trim();
    if (form.appSecret.trim()) corpo.appSecret = form.appSecret.trim();
    if (Object.keys(corpo).length) salvar.mutate(corpo);
  }

  const webhook = `${window.location.origin}${o.caminhoWebhook ?? '/api/whatsapp/webhook'}`;

  async function copiar(rotulo, texto) {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(rotulo);
      setTimeout(() => setCopiado(null), 2000);
    } catch {
      setCopiado(null);
    }
  }

  const erros = salvar.error?.camposComErro ?? {};

  return (
    <Cartao titulo="Credenciais da API oficial">
      <form className="cx-config" onSubmit={gravar}>
        <p className="texto-fraco">
          Copie os dados do app no painel da Meta (developers.facebook.com → seu app → WhatsApp → Configuração da API).
        </p>

        <Campo rotulo="Phone Number ID" obrigatorio erro={erros.phoneNumberId}>
          <Entrada value={form.phoneNumberId} onChange={mudar('phoneNumberId')} inputMode="numeric" disabled={!podeEditar} placeholder="Ex.: 109876543210" />
        </Campo>
        <Campo rotulo="WhatsApp Business Account ID (opcional)" erro={erros.wabaId}>
          <Entrada value={form.wabaId} onChange={mudar('wabaId')} inputMode="numeric" disabled={!podeEditar} />
        </Campo>
        <Campo
          rotulo="Token de acesso permanente"
          obrigatorio
          erro={erros.token}
          dica={o.temToken ? `Salvo (termina em ${o.tokenFinal}). Deixe em branco para manter.` : 'Use um token de usuário do sistema, que não expira.'}
        >
          <Entrada type="password" autoComplete="off" value={form.token} onChange={mudar('token')} disabled={!podeEditar} />
        </Campo>
        <Campo
          rotulo="App Secret"
          obrigatorio
          erro={erros.appSecret}
          dica={o.temAppSecret ? 'Salvo. Deixe em branco para manter.' : 'Confere que as mensagens vêm mesmo da Meta.'}
        >
          <Entrada type="password" autoComplete="off" value={form.appSecret} onChange={mudar('appSecret')} disabled={!podeEditar} />
        </Campo>

        {salvar.isError && (
          <Aviso tom="perigo" aoFechar={salvar.reset}>
            {salvar.error.message}
          </Aviso>
        )}
        {salvar.isSuccess && <Aviso tom="sucesso">Credenciais salvas. Agora clique em “Conectar número”.</Aviso>}

        {podeEditar && (
          <div className="linha">
            <Botao type="submit" carregando={salvar.isPending}>
              Salvar credenciais
            </Botao>
          </div>
        )}
      </form>

      {podeEditar && o.verifyToken && (
        <div className="cx-webhook">
          <p className="cx-webhook__titulo">Cole no painel da Meta (Webhook)</p>
          <div className="cx-webhook__linha">
            <span className="texto-fraco">URL de retorno</span>
            <code className="mono">{webhook}</code>
            <Botao type="button" variante="secundario" tamanho="sm" onClick={() => copiar('url', webhook)}>
              {copiado === 'url' ? 'Copiado!' : 'Copiar'}
            </Botao>
          </div>
          <div className="cx-webhook__linha">
            <span className="texto-fraco">Verify token</span>
            <code className="mono">{o.verifyToken}</code>
            <Botao type="button" variante="secundario" tamanho="sm" onClick={() => copiar('token', o.verifyToken)}>
              {copiado === 'token' ? 'Copiado!' : 'Copiar'}
            </Botao>
          </div>
          <p className="texto-fraco">Assine o campo “messages”. O endereço precisa ser público com HTTPS (servidor na nuvem).</p>
        </div>
      )}
    </Cartao>
  );
}
